import React, { useEffect, useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Stack,
    FormControl,
    TextField,
    IconButton,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';

interface ProductFlowCredentialsFormProps {
    onNewConnection: (connection: Connection) => void;
    onClose: () => void;
    /** When set, re-authorizes this existing connection instead of creating a new one. */
    reconnectConnectionId?: string;
}

const formatTarget = (target: string) =>
    target.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ProductFlowCredentialsForm: React.FC<ProductFlowCredentialsFormProps> = ({ onNewConnection, onClose, reconnectConnectionId }) => {
    const [xCompanyId, setXCompanyId] = useState('');
    const [acceptLanguage, setAcceptLanguage] = useState('');
    const [personalAccessTokenKey, setPersonalAccessTokenKey] = useState('');

    const [validationError, setValidationError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const addJob = useImportJobsStore((s) => s.addJob);

    const seedImportJobs = async (connection: Connection) => {
        const jobsResult = await container.importJobsRepository.fetchActiveJobs();
        if (jobsResult.status !== 'success') return;

        jobsResult.data
            .filter((job) => job.connectionId === connection.id)
            .forEach((job) => {
                addJob({
                    id: job.jobId,
                    connector: job.connector,
                    label: `${connection.name} · ${formatTarget(job.target)}`,
                    startedAt: job.startedAt,
                });
            });
    };

    const connectWithProductFlow = async () => {
        setError(null);
        setValidationError(null);

        if (!xCompanyId || !acceptLanguage || !personalAccessTokenKey) {
            setValidationError('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        try {
            const result = reconnectConnectionId
                ? await container.productFlowConnectorRepository.reconnect(reconnectConnectionId, xCompanyId, acceptLanguage, personalAccessTokenKey)
                : await container.productFlowConnectorRepository.addProductFlowConnection(xCompanyId, acceptLanguage, personalAccessTokenKey);

            if (result.status === 'error') {
                setError(result.error.message || 'Failed to connect to ProductFlow');
                setIsLoading(false);
                return;
            }

            await seedImportJobs(result.data);
            onNewConnection(result.data);
        } catch (err) {
            setError('Failed to connect to ProductFlow. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="medium">
                        {reconnectConnectionId ? 'Reconnect ProductFlow' : 'Connect ProductFlow'}
                    </Typography>

                    <IconButton onClick={onClose} color="inherit">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent>

                <Box>
                    <Typography variant="subtitle2" color="text.primary">
                        To connect to your ProductFlow account, follow these steps:
                    </Typography>


                    <Typography variant="body2" color="text.secondary">
                        <ol style={{ paddingLeft: 20 }}>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Log in to your <a href="https://cloud.productflow.com/profile" target="_blank" rel="noopener noreferrer">ProductFlow</a> portal
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    In the right top corner, click on your name and navigate to Profile
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    In the API section copy the X-Company-id and Accept-Language values below
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Click the + icon next to "Personal access tokens" to create a personal access token
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Fill the generated personal access token in the form below
                                </Typography>
                            </li>


                        </ol>
                    </Typography>
                </Box>

                <FormControl fullWidth>
                    <TextField
                        label="X-Company-id"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={xCompanyId}
                        onChange={(e) => setXCompanyId(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                    />
                    <TextField
                        label="Accept-Language"
                        type="text"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={acceptLanguage}
                        onChange={(e) => setAcceptLanguage(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                    />

                    <TextField
                        label="Personal Access Token"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={personalAccessTokenKey}
                        onChange={(e) => setPersonalAccessTokenKey(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                    />


                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}


                </FormControl>


            </DialogContent >


            < DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3, justifyContent: 'flex-end' }}>

                <Stack direction="row" alignItems="center" gap={1} flex={1} justifyContent="space-between">
                    <Button variant="outlined" onClick={onClose}>Back</Button>
                    <Button
                        variant="contained"
                        disabled={isLoading || !xCompanyId || !acceptLanguage || !personalAccessTokenKey}
                        onClick={connectWithProductFlow}
                    >
                        {reconnectConnectionId ? 'Reconnect' : 'Connect'}
                    </Button>
                </Stack>

            </DialogActions >


        </>
    );
};

export default ProductFlowCredentialsForm;